import { useCallback, useMemo, useState } from 'react';
import { Action } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { useMap } from 'react-use';

import { dropTempSettings, saveTempSetting } from 'actions/configs';
import { getSavedSettings, getSettings } from 'reducers/configs/settings';
import { RootState } from 'reducers/index';

export function useSettings() {
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();

  const [changed, setChanged] = useState({});
  const [
    errors,
    { get: getError, set: setError, remove: removeError },
  ] = useMap<Record<string, string>>();
  const current = useSelector(getSettings);
  const saved = useSelector(getSavedSettings);

  const onChange = useCallback(
    (key, value) => {
      if (String(saved[key]) === String(value)) {
        setChanged(state => {
          dispatch(dropTempSettings([key]));
          const newState = { ...state };
          delete newState[key];
          return newState;
        });
      } else {
        dispatch(saveTempSetting(key, value));
        setChanged(state => ({ ...state, [key]: value }));
      }
      return undefined;
    },
    [current],
  );

  const values = useMemo(
    () => ({
      ...current,
      ...changed,
    }),
    [current, changed],
  );

  const allValid = useMemo(() => !Object.keys(errors).length, [errors]);

  return {
    values,
    onChange,
    changed,
    getError,
    setError,
    removeError,
    allValid,
  };
}
